// Removing an email connection from an ongoing (busy-my-web#105).
//
// Frames 40008459:190428 → 40007359:262096 (section 40007296:252559). An ongoing made out of email
// groups FOLLOWS those senders; each one is a "connection". This is the journey that ends one:
//
//   the Briefing's "2 email connections" row is clicked (it becomes the frames' closeable filter
//   chip)  →  the middle lists the connections ("Group name" / "Emails" / "Last update")  →  a row
//   is ticked, and the Briefing says "1 item selected, 14 emails"  →  the suggestion panel offers
//   "Remove selected email connection"  →  "Next" opens the confirmation  →  "Remove" does it, and
//   a green "Email connection removed." band replaces the panel.
//
// Every string a frame writes is HERE rather than in a template, for the reason lib/emailSession.js
// gives: the same sentence appears in the strip, the listing and the story, and one module is what
// keeps them from drifting. Pure — no store, no router, nothing mounted.
//
// What REMOVING actually does (busy-timeline-srv `DELETE v1/ongoing/{id}/followedSenders`): the
// follow is ended, so no FUTURE mail from that sender joins the ongoing. The emails it already
// brought in stay linked — the story keeps them, and the "N emails" count on the briefing's email
// row does not move. What moves is the connections count, and the story gains a permanent
// "Email connection removed" step. Nothing here pretends otherwise.

import { emailsLabel, lastUpdate } from '@/lib/emailSession'
import { capitalAfterCount, countLabel } from '@/lib/briefings/rows'
// The rail's "Filter opened" node — one sentence, written once, wherever a Briefing filter opens.
import { filterRailStep } from '@/lib/researchRailCopy'

/** Verbatim design strings (frames 2-6 of the section). */
export const TEXT = {
  // The middle's listing (frame 40008461:190811).
  listingTitle: 'Email connections',
  groupNameColumn: 'Group name',
  emailsColumn: 'Emails',
  lastUpdateColumn: 'Last update',
  // The strip (frames 40008461:192381 → 40008462:194231).
  suggestionsTitle: 'What would you like to do?',
  removeOption: 'Remove selected email connection',
  // …and the offer the frames make with NOTHING ticked (2026-09-15 parity audit F2-6,
  // busy-my-web#500, frame 40008461:192381): the box lists the connections and offers to remove
  // them all, rather than offering nothing at all until something is picked.
  removeAllOption: 'Remove email connections',
  reply: 'Reply',
  forward: 'Forward',
  next: 'Next',
  remove: 'Remove',
  clearSelections: 'Clear selections',
  view: 'View',
  confirmTitle: 'Remove email connections',
  confirmQuestion: 'Do you want to remove email connection?',
  // The frame writes "Every future email from this groups will not be linked to this Ongoing." —
  // the number is data here, so the sentence agrees with what is actually selected (see
  // `confirmCopy`); the frame's mixed "this groups" is the only word changed.
  confirmHelperOne: 'Every future email from this group will not be linked to this Ongoing.',
  confirmHelperMany: 'Every future email from these groups will not be linked to this Ongoing.',
  // The receipt (frame 40007359:262096), full stop included — the frame writes one.
  removed: 'Email connection removed.',
  removeFailed: 'Could not remove the email connection. Try again.',
  boardHeading: 'Selected',
}

/** A connection's display name — the sender as it was picked, else the bare address. */
export function connectionName(connection) {
  if (!connection) return ''
  return String(connection.displaySender || connection.emailAddress || '').trim()
}

/** "2 email connections" — the Briefing's own row, and the filter chip it becomes. */
export function connectionsLabel(count) {
  // "2 Email connections" — a Briefing row, capitalised after the count (busy-my-web#609).
  return countLabel(count, 'email connection')
}

/**
 * One row of the middle's listing: the three columns the frame heads, and nothing else.
 *
 * "Last update" is the connection's own newest mail (`lastEmailDate`, served with the story) shown
 * the way every other email time in the app is — a time today, a day and month before that. Blank
 * when the sender has not written yet: the column says nothing rather than standing in the day the
 * connection happened to be made.
 */
export function connectionRow(connection, now = new Date()) {
  return {
    address: String(connection?.emailAddress || ''),
    name: connectionName(connection),
    emails: emailsLabel(connection?.emailCount),
    count: Number(connection?.emailCount) || 0,
    updated: lastUpdate(connection?.lastEmailDate ?? null, now),
  }
}

/** The whole listing, in the order the story sends it (oldest connection first). */
export function connectionRows(connections, now = new Date()) {
  return (connections || []).filter(Boolean).map(c => connectionRow(c, now))
}

// ── What is selected ────────────────────────────────────────────────────────────────────────────
// On the Work-board, like every other selection in the app (Fatemeh's rule from #99: anything
// selected enters the Work-board and the timeline) — which is also what draws the frames' "Selected
// / 14 Emails" panel beside the box, with the connection's own removable chip in it.

/** The board id for a connection, so ticking it twice is the same tick. */
export function connectionPickId(address) { return `email-connection:${String(address || '')}` }

/** A ticked connection, as it goes onto the Work-board. */
export function connectionPick(connection) {
  return {
    id: connectionPickId(connection?.emailAddress),
    kind: 'email-connection',
    address: String(connection?.emailAddress || ''),
    title: connectionName(connection),
    count: Number(connection?.emailCount) || 0,
  }
}

/** Only the connection picks — the board carries research items and email groups too. */
export function connectionPicks(items) {
  return (items || []).filter(i => i && i.kind === 'email-connection')
}

/**
 * The connections that are ticked, resolved against the story's LIVE list.
 *
 * Resolved rather than stored, for the reason the email session resolves its open group: the count
 * and the name belong to the story, and a pick left over from another ongoing (or from a connection
 * that has just been removed) resolves to nothing instead of to a stale row.
 */
export function selectedConnections(connections, items) {
  const picked = new Set(connectionPicks(items).map(p => p.id))
  return (connections || []).filter(c => c && picked.has(connectionPickId(c.emailAddress)))
}

/** How many emails the ticked connections stand for — the number every selection line quotes. */
export function selectedEmailCount(selected) {
  return (selected || []).reduce((sum, c) => sum + (Number(c.emailCount) || 0), 0)
}

/**
 * The Briefing's selection line — the frame's two spans: "1 item selected," then "14 emails".
 * Null with nothing ticked, so the line is absent rather than reading "0 items selected,".
 */
export function selectionSummary(selected) {
  const list = selected || []
  if (!list.length) return null
  return {
    // The Briefing's own line, so "1 Item selected, 14 Emails" (busy-my-web#609).
    items: `${countLabel(list.length, 'item')} selected,`,
    emails: capitalAfterCount(emailsLabel(selectedEmailCount(list))),
  }
}

/** The Work-board's heading pair: "Selected" over "14 Emails" (capital E, as the frame writes it). */
export function boardSummary(selected) {
  const total = selectedEmailCount(selected)
  if (!total) return null
  return { heading: TEXT.boardHeading, count: `${total} Email${total === 1 ? '' : 's'}` }
}

// ── What the RAIL says about this journey (busy-my-web#505) ─────────────────────────────────────
// Frames 40008461:190811 (filter opened) and 40008461:192381 (a connection ticked). Opening the
// Briefing's "N email connections" row and ticking a connection are both things that have HAPPENED
// to this story, so the rail ends in them — exactly as it already does for the research-pack filter
// (lib/researchRailCopy's `filterRailStep`) and for a pack selection (lib/packCompare's
// `selectionRailStep`). This is that same pair of nodes, asked about connections, in one function
// because the rail only ever draws ONE of them: a selection is the later move, and the rail reports
// the latest one.

/** The rail's own heading for this journey — the listing's title, so the two cannot drift. */
export const RAIL_TITLE = TEXT.listingTitle
/** What the filter node invites (frame 40008461:190811, verbatim). */
export const RAIL_FILTER_NOUN = 'connection'

/**
 * The rail node for the email-connections filter.
 *
 * @param open     is the Briefing's "N email connections" filter in force?
 * @param selected the ticked connections (store `storyConnections/selected`)
 * @returns null with the filter closed; otherwise
 *          { title, sub, detail, items } — `items` empty for the "Filter opened" node, and one chip
 *          per ticked connection ({ id, title, detail }) for the "N items selected" one.
 */
export function connectionsRailStep({ open = false, selected = [] } = {}) {
  if (!open) return null
  const picked = (selected || []).filter(Boolean)
  if (!picked.length) {
    // Same node the research filter draws, worded for this one — "Select a connection to view it
    // here" rather than the singularised heading, which would read "a email connection".
    return { ...filterRailStep({ open: true, kindPlural: RAIL_TITLE, noun: RAIL_FILTER_NOUN }), items: [] }
  }
  return {
    title: RAIL_TITLE,
    sub: `${picked.length} item${picked.length === 1 ? '' : 's'} selected`,
    detail: '',
    // The frame's chips: the sender's name, and how much mail it stands for.
    items: picked.map(c => ({
      id: connectionPickId(c.emailAddress),
      title: connectionName(c),
      detail: emailsLabel(c.emailCount),
    })),
  }
}

// ── The suggestion panel ────────────────────────────────────────────────────────────────────────

/**
 * "What would you like to do?" — the three options the frame offers, in its order. Choosing one
 * ARMS it (the filled radio); the primary button then carries it out, so removing always takes two
 * gestures and, past "Next", three.
 *
 * Reply and Forward are the story's own answers, offered here because the frame offers them; they
 * are only READY when the ongoing actually holds an email thread to answer — with none, there is
 * nothing to reply to, and an option wired to nothing is worse than a greyed one (#99's rule).
 *
 * @param selected   the ticked connections
 * @param emailCount how many email threads the story holds
 */
export function suggestions({ selected = [], emailCount = 0, connections = [] } = {}) {
  // AT REST (2026-09-15 parity audit F2-6, busy-my-web#500): the frames still offer something —
  // "Remove email connections", about all of them — so the panel is never a question with no
  // answers under it. It only appears where there ARE connections to remove.
  if (!(selected || []).length) {
    return (connections || []).length
      ? [{ id: 'remove-all-connections', text: TEXT.removeAllOption, recommended: false, ready: true }]
      : []
  }
  const canAnswer = (Number(emailCount) || 0) > 0
  return [
    { id: 'remove-connection', text: TEXT.removeOption, recommended: true, ready: true },
    { id: 'reply', text: TEXT.reply, recommended: false, ready: canAnswer },
    { id: 'forward', text: TEXT.forward, recommended: false, ready: canAnswer },
  ]
}

/** Which armed option leads to the removal confirmation (the others act immediately). */
export function removesOnCommit(id) { return id === 'remove-connection' || id === 'remove-all-connections' }

/** …and which of them is the ALL variant, which ticks every connection before it asks (F2-6). */
export function removesAllOnCommit(id) { return id === 'remove-all-connections' }

/**
 * The confirmation panel (frame 40008462:194231): the question, and what removing actually costs.
 * Null with nothing selected — the panel has nothing to ask about then.
 */
export function confirmCopy(selected) {
  const list = selected || []
  if (!list.length) return null
  return {
    title: TEXT.confirmTitle,
    question: TEXT.confirmQuestion,
    helper: list.length === 1 ? TEXT.confirmHelperOne : TEXT.confirmHelperMany,
  }
}

/** The primary button's word: "Next" while choosing, "Remove" once it is asking (the frames' own change). */
export function removeCta(stage) { return stage === 'confirm' ? TEXT.remove : TEXT.next }

/** A removal needs at least one ticked connection — nothing else. */
export function canRemove(selected) { return (selected || []).length > 0 }

/** The addresses a "Remove" would unfollow, in the order they were listed. */
export function removeTargets(selected) {
  return (selected || []).map(c => String(c?.emailAddress || '')).filter(Boolean)
}

/**
 * The path the removal calls, per address (`ongoing/{id}/followedSenders?sender=…` through the
 * BFF). Built here so what is about to be sent can be checked without sending it.
 */
export function removePath(address) {
  return `followedSenders?sender=${encodeURIComponent(String(address || ''))}`
}

/**
 * The band that replaces the panel (frame 40007359:262096): the green receipt when it lands, an
 * honest line when it does not. Nothing while idle or in flight — the removal is one call and the
 * panel is still on screen, so a spinner band would only flash.
 */
export function removeNotice(status, ongoingId = '') {
  // The receipt carries a "View" onto the story it changed (2026-09-15 parity audit F2-6,
  // busy-my-web#500, frame 40007359:262096) — the same green-band-with-View shape every other
  // receipt in this estate wears since #494.
  const id = String(ongoingId || '').trim()
  if (status === 'removed') return { kind: 'removed', text: TEXT.removed, view: id ? `/ongoing/${id}` : '' }
  if (status === 'error') return { kind: 'error', text: TEXT.removeFailed }
  return null
}
